import React, { useState, useMemo } from 'react';
import { ShareFat, Pencil } from '@phosphor-icons/react';
import classNames from 'classnames';

import { ViewAllBtn } from '../../../components/custom';
import { Button } from '../../../components/general';

import AttendeePanel from './attendeePanel';
import { ExtendedEvent, getAssetsFile } from './getData';

interface Props {
  event: ExtendedEvent;
  isCreationAccess: boolean;
  isClipsAccess: boolean;
}

const VideoPanels: React.FC<Props> = (props) => {
  const { event, isCreationAccess, isClipsAccess } = props;

  const [isCollapsed, setIsCollapsed] = useState(true);

  const MAX_CLIPS = 4;
  const isPortrait = event?.videoMode === 'portrait';

  const clips = useMemo(() => {
    const clips = event?.clips ?? [];

    return isCollapsed && clips.length > MAX_CLIPS
      ? clips.slice(0, MAX_CLIPS - 1)
      : clips;
  }, [MAX_CLIPS, event?.clips, isCollapsed]);

  const creationWidth = classNames({
    'w-[calc((var(--page-width)_-_2.5rem)_/_2)]': !isPortrait,
    'w-[calc((var(--page-width)_-_2.5rem)_/_4)]': isPortrait,
  });

  const clipWidth = classNames({
    'w-[calc((100%_-_3rem)_/_2)]': !isPortrait,
    'w-[calc((100%_-_3rem)_/_4)]': isPortrait,
  });

  const videoClass = classNames({
    'rounded-md': true,
    'aspect-video': !isPortrait,
    'aspect-[9/16]': isPortrait,
  });

  return (
    <div className="flex gap-10">
      <AttendeePanel
        heading="Your Highlight Reel"
        className={classNames(creationWidth, 'h-min shrink-0')}
      >
        <video
          className={classNames(videoClass, 'w-full bg-black')}
          src={getAssetsFile(
            `creation${isCreationAccess ? '' : '-preview'}.mp4`,
            event.eventId,
          )}
          poster={getAssetsFile(
            `creation${isCreationAccess ? '' : '-preview'}-poster.png`,
            event.eventId,
          )}
          controls
          controlsList="nodownload"
        />

        {isCreationAccess && (
          <div className={classNames('flex gap-4', { 'flex-col': isPortrait })}>
            <Button
              variant="animated-border"
              colour="custom"
              className="h-[var(--form-el-height)] w-full running-glow z-[1]"
            >
              <div className="w-full h-full flex items-center justify-center p-2 gap-2 bg-grey-100 group-hover:bg-transparent rounded">
                <ShareFat
                  weight="bold"
                  size="1.25rem"
                  className="fill-[url('#icon-purple-gradient-2')] group-hover:fill-white"
                />

                <div className="font-semibold bg-gradient-purple-light bg-clip-text text-transparent group-hover:text-white">
                  Share
                </div>
              </div>
            </Button>

            <Button
              colour="black-outline"
              className="w-full font-semibold"
              withGap
            >
              <Pencil size="1.25rem" />
              <div>Edit for $10</div>
            </Button>
          </div>
        )}
      </AttendeePanel>

      <AttendeePanel heading="Clips Featuring You" className="flex-grow">
        <div className="flex flex-wrap gap-4">
          {clips.map((index) => (
            <div
              key={index}
              className={classNames(
                clipWidth,
                videoClass,
                'relative overflow-hidden',
              )}
            >
              <video
                className="w-full h-full bg-black"
                src={getAssetsFile(`clip-${index}.mp4`, event.eventId)}
                autoPlay
                muted
                loop
                playsInline
              />

              {!isClipsAccess && (
                <img
                  className="absolute top-0 left-0 w-full h-full z-10"
                  src={getAssetsFile(
                    `${isPortrait ? 'portrait' : 'landscape'}-watermark-diagonal.png`,
                    undefined,
                    'watermark',
                  )}
                  alt="Watermark"
                />
              )}
            </div>
          ))}

          {event.clips.length > MAX_CLIPS && (
            <ViewAllBtn
              isCollapsed={isCollapsed}
              setIsCollapsed={setIsCollapsed}
              isPortrait={isPortrait}
              customWidth={clipWidth}
            />
          )}
        </div>
      </AttendeePanel>
    </div>
  );
};

export default VideoPanels;
